import axios from 'axios';
import { API } from '@/config';

// In-flight prefetch promises
const _promises = {};
// Resolved data (available synchronously once loaded)
const _resolved = {};

// Map HTML prefetch endpoints to our keys - uses dynamic language
function getHTMLPrefetchMap() {
  const lang = localStorage.getItem('i18nextLng') || 'sv';
  return {
    '/api/lessons': 'lessons',
    [`/api/traffic-signs?lang=${lang}`]: 'signs',
    [`/api/quick-tips?lang=${lang}`]: 'quickTips',
    '/api/subscription-plans/public': 'plans',
    '/api/reviews/public?limit=12': 'reviews',
  };
}

// Consume HTML-level prefetch (started before React loads)
function consumeHTMLPrefetch() {
  if (typeof window !== 'undefined' && window.__PREFETCH__) {
    const map = getHTMLPrefetchMap();
    Object.entries(map).forEach(([endpoint, key]) => {
      if (window.__PREFETCH__[endpoint] && !_promises[key]) {
        _promises[key] = window.__PREFETCH__[endpoint]
          .then(data => { if (data) _resolved[key] = data; return data; })
          .catch(() => null);
      }
    });
    delete window.__PREFETCH__;
  }
}

// Run immediately on module load
consumeHTMLPrefetch();

// Start prefetching all critical page data
export function prefetchAllData() {
  const lang = localStorage.getItem('i18nextLng') || 'sv';
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const prefetch = (key, url, opts = {}) => {
    if (!_promises[key]) {
      _promises[key] = axios.get(url, opts)
        .then(r => { _resolved[key] = r.data; return r.data; })
        .catch(() => null);
    }
  };

  prefetch('tests', `${API}/tests/available?lang=${lang}`, { headers });
  prefetch('lessons', `${API}/lessons`);
  prefetch('signs', `${API}/traffic-signs?lang=${lang}`);
  prefetch('quickTips', `${API}/quick-tips?lang=${lang}`);
  prefetch('reviews', `${API}/reviews/public?limit=12`);
  prefetch('plans', `${API}/subscription-plans/public`);
}

// Check if a prefetch is in-flight or resolved
export function hasPrefetch(key) {
  return !!_promises[key] || !!_resolved[key];
}

// Get resolved data synchronously (for useState initializers)
export function getResolvedData(key) {
  return _resolved[key] || null;
}

// Get prefetched data (async - waits for promise if still loading)
export async function getPrefetched(key) {
  if (_resolved[key]) return _resolved[key];
  if (_promises[key]) return await _promises[key];
  return null;
}

// Detail prefetch - per item (on hover over cards)
export function prefetchDetail(type, id, extraHeaders = {}) {
  const key = `${type}_${id}`;
  if (!_promises[key]) {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}`, ...extraHeaders } : extraHeaders;

    const urls = {
      lesson: `${API}/lessons/${id}`,
      sign: `${API}/traffic-signs/${id}`,
      test: `${API}/tests/${id}`,
      tip: `${API}/quick-tips/${id}`,
    };
    const url = urls[type];
    if (url) {
      _promises[key] = axios.get(url, { headers })
        .then(r => { _resolved[key] = r.data; return r.data; })
        .catch(() => null);
    }
  }
}

export function prefetchPageData(path) {
  const lang = localStorage.getItem('i18nextLng') || 'sv';
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const prefetch = (key, url, opts = {}) => {
    if (!_promises[key]) {
      _promises[key] = axios.get(url, opts)
        .then(r => { _resolved[key] = r.data; return r.data; })
        .catch(() => null);
    }
  };

  if (path.includes('ovningsprov')) prefetch('tests', `${API}/tests/available?lang=${lang}`, { headers });
  else if (path.includes('teorilektioner')) prefetch('lessons', `${API}/lessons`);
  else if (path.includes('trafikmarken')) prefetch('signs', `${API}/traffic-signs?lang=${lang}`);
  else if (path.includes('snabba-rad')) prefetch('quickTips', `${API}/quick-tips?lang=${lang}`);
  else if (path.includes('recensioner')) prefetch('reviews', `${API}/reviews/public?limit=12`);
  else if (path.includes('priser')) prefetch('plans', `${API}/subscription-plans/public`);
}
